import { Flex, Icon, Stack, Tooltip } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Heading18 } from "components/Heading";
import { Text14 } from "components/Text";
import { useCallback } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PrimaryButton, SecondaryButton } from "components/Button";
import SwitchField from "components/FormElements/Fields/SwitchField";
import MeetingTemplateTimeSlotField from "../../../Fields/MeetingTemplateTimeSlotField";
import MeetingDurationField from "../../MeetingDurationField";
import SchedulingParameters from "../SchedulingParameters";
import { meetingTemplateTimingSchema, MeetingTemplateTimingSchema } from "./schema";

export default function MeetingTimingForm({
  form: parentForm,
  onClose,
}: {
  form: UseFormReturn<any>;
  onClose: () => void;
}) {
  const useSpecificTimeInitial = parentForm.getValues("useSpecificTime") ?? false;
  // Create child form with its own schema
  const childForm = useForm<MeetingTemplateTimingSchema>({
    resolver: yupResolver(meetingTemplateTimingSchema),
    defaultValues: {
      durationMinutes: parentForm.getValues("durationMinutes") || 30,
      offset: parentForm.getValues("offset") || 0,
      referenceEvent: parentForm.getValues("referenceEvent"),
      offsetUnit: parentForm.getValues("offsetUnit"),
      allowWeekends: parentForm.getValues("allowWeekends") ?? false,
      useSpecificTime: useSpecificTimeInitial,
      specificTimeOfDay: parentForm.getValues("specificTimeOfDay") ?? null,
      preferredTimeSlot: useSpecificTimeInitial ? null : parentForm.getValues("preferredTimeSlot") || "morning",
    },
  });

  const {
    setValue,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = childForm;

  const onSubmit = useCallback(
    (values: MeetingTemplateTimingSchema) => {
      // Propagate validated values to parent form
      Object.entries(values).forEach(([key, value]) => {
        parentForm.setValue(key as any, value, { shouldDirty: true });
      });
      onClose();
    },
    [parentForm, onClose],
  );

  const onError = useCallback((validationErrors: any) => {
    console.error("Timing validation failed", validationErrors);
  }, []);

  const [offset, referenceEvent] = watch(["offset", "referenceEvent"]);

  return (
    <Stack py="6" p="2" spacing="4">
      <Heading18 mb={4}>Meeting Timing</Heading18>
      <MeetingDurationField form={childForm} fieldName="durationMinutes" label="Duration (minutes)" isRequired />
      <Text14 pt="4">
        Select timing relative to the New Hire{" "}
        <Text14 as="span" fontWeight="700">
          Start Date
        </Text14>
      </Text14>
      <SchedulingParameters
        offset={offset || 0}
        referenceEvent={referenceEvent}
        setValue={setValue}
        control={control as any}
        errors={errors}
      />
      <MeetingTemplateTimeSlotField form={childForm} />
      <SwitchField
        name="allowWeekends"
        label={
          <Flex align="center" gap={2}>
            Allow weekend scheduling
            <Tooltip
              label={
                "When disabled, weekend dates are moved to weekdays. " +
                "Dates scheduled before an event move to the Friday before the weekend. " +
                "Dates scheduled after an event move to the Monday after the weekend."
              }
              fontSize="sm"
              placement="top"
              hasArrow
            >
              <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.500" cursor="help" />
            </Tooltip>
          </Flex>
        }
        control={control as any}
        errors={errors}
      />
      <Flex pt="4" w="100%" justify="flex-end" gap="2">
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleSubmit(onSubmit, onError)}>Save</PrimaryButton>
      </Flex>
    </Stack>
  );
}
